import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { AnchorIcon, ShieldIcon, UsersIcon } from 'lucide-react';

export const About = () => {
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const features = entry.target.querySelectorAll('.feature-card');
            features.forEach((feature, index) => {
              setTimeout(() => {
                feature.classList.add('animate-feature');
              }, index * 150);
            });
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }

    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
    };
  }, []);

  return (
    <section id="nosotros" className="section bg-white relative overflow-hidden">
      {/* Decorative elements animados */}
      <motion.div
        className="absolute top-20 right-10 w-64 h-64 bg-turquoise/5 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-40 left-10 w-96 h-96 bg-navy/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.1, 1],
          x: [0, -20, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="container relative z-10">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="inline-block text-turquoise font-medium mb-3 tracking-wider uppercase text-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            DESCUBRE
          </motion.span>

          <motion.h2
            className="font-playfair text-3xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            La Experiencia{' '}
            <span className="text-turquoise">Perla del Pacífico</span>
          </motion.h2>

          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-turquoise to-navy mx-auto mb-8"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </motion.div>
        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="space-y-6">
            <motion.p
              className="text-xl font-playfair mb-6 text-navy"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              En{' '}
              <span className="text-turquoise font-semibold">
                Perla del Pacífico
              </span>{' '}
              te invitamos a descubrir la esencia de Guayaquil navegando sobre el
              majestuoso río Guayas.
            </motion.p>

            <motion.p
              className="mb-6 text-lg text-gray-700"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Desde el Muelle del Yacht Club Naval, disfruta de un paseo íntimo y
              seguro, rodeado de vistas espectaculares del skyline y los
              atardeceres mágicos que pintan el cielo de la Perla del Pacífico.
            </motion.p>

            <motion.p
              className="mb-10 text-gray-700"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Nuestro recorrido te permite apreciar la belleza natural del río
              mientras te sumerges en la historia y cultura de Guayaquil, todo
              desde una perspectiva privilegiada que solo el agua puede ofrecer.
            </motion.p>

            {/* Feature Cards */}
            <div
              ref={featuresRef}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
            >
              {[
                {
                  icon: AnchorIcon,
                  title: 'Paisajes únicos',
                  description:
                    'Vistas panorámicas exclusivas del skyline de Guayaquil y atardeceres inolvidables sobre el río Guayas',
                },
                {
                  icon: ShieldIcon,
                  title: 'Seguridad garantizada',
                  description:
                    'Capitanes certificados y embarcaciones con todos los protocolos de seguridad internacional',
                },
                {
                  icon: UsersIcon,
                  title: 'Atención personalizada',
                  description:
                    'Grupos reducidos para brindarte una experiencia íntima y atención de primer nivel',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex flex-col items-center text-center p-6 rounded-lg transition-all duration-300 hover:bg-turquoise/5"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-turquoise to-navy rounded-full flex items-center justify-center mb-4 shadow-lg"
                    whileHover={{ rotate: 12, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="font-playfair font-semibold text-lg mb-3 text-navy">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Image Grid */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  src: '/assets/malecon-2000.jpg',
                  alt: 'Malecón 2000 de Guayaquil',
                  label: 'Malecón 2000',
                  delay: 0,
                },
                {
                  src: '/assets/rio-guayas.jpg',
                  alt: 'Río Guayas en Guayaquil',
                  label: 'Río Guayas',
                  delay: 0.1,
                },
                {
                  src: '/assets/malecon-rio.webp',
                  alt: 'Vista del Malecón desde el río',
                  label: 'Vista desde el agua',
                  delay: 0.2,
                },
                {
                  src: '/assets/guayaquil.jpg',
                  alt: 'Paseo en lancha Guayaquil',
                  label: 'Experiencia náutica',
                  delay: 0.3,
                },
              ].map((image, index) => (
                <motion.div
                  key={index}
                  className={`relative overflow-hidden rounded-xl shadow-lg group cursor-pointer ${
                    index === 0 || index === 3 ? 'h-64' : 'h-48'
                  }`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: image.delay }}
                  whileHover={{ scale: 1.05, zIndex: 10 }}
                >
                  <motion.img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.4 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/20 to-transparent" />
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 p-3 text-white"
                    initial={{ y: 20, opacity: 0 }}
                    whileHover={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="text-sm font-medium">{image.label}</span>
                  </motion.div>
                  {/* Border effect */}
                  <motion.div
                    className="absolute inset-0 border-2 border-turquoise/0 rounded-xl"
                    whileHover={{ borderColor: 'rgba(0, 168, 204, 0.6)' }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.div>
              ))}
            </div>

            {/* Decorative elements */}
            <motion.div
              className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-turquoise rounded-xl -z-10"
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <motion.div
              className="absolute -top-4 -left-4 w-32 h-32 border-4 border-navy rounded-xl -z-10"
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2,
              }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};